import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChevronDown, ChevronUp, Columns3, Download, Star } from 'lucide-react';
import { useWatchlistAsScreener, useWatchlists } from '../../api/hooks.js';
import { PanelFrame } from '../panel-frame.js';
import { PanelEmptyState } from '../panel-empty-state.js';
import { EntityRowContextMenu } from '../entity-row-context-menu.js';
import { ENTITY_MIME } from '../dnd.js';
import { WatchlistPicker } from '../watchlist-picker.js';
import {
  SCREENER_COLUMN_DEFS,
  renderCell,
  type ScreenerRightColumnId,
  type ScreenerColumnState,
} from '../screener-columns.js';
import { EMPTY_VALUE } from '../../format.js';
import {
  useScreenerColumnConfig,
  type ScreenerColumnConfig,
  type ScreenerColumnConfigActions,
} from '../use-screener-column-config.js';
import { ColumnsMenu } from '../columns-menu.js';
import { ColumnHeaderCell } from '../column-header-cell.js';
import { useDockviewApi } from '../dockview-context.js';
import { useCatchmentKey } from '../catchment-link.js';
import { useSelectLink } from '../select-link.js';
import {
  useActiveCountry,
  useAppStore,
  useGlobalCurrency,
  usePanelEntityId,
  useSetPanelEntity,
  type PanelId,
  type TabId,
} from '../store.js';
import type { ScreenerRow } from '../../api/screener-client.js';
import type { WatchlistRow } from '../../api/client.js';
import type { GlobalCurrency } from '../../currency.js';

// Watchlist panel — visual replica of the Screener, scoped to the
// selected watchlist's members. Data comes from the Screener endpoint
// filtered by entity ids (see useWatchlistAsScreener) so the column
// system, cell renderer, typography, and row-quality flags stay 1:1
// with the Screener. The only thing different here is which rows the
// table contains (a saved subset) and the picker chrome at the top
// that lets the user switch between named watchlists.

const ROW_HEIGHT = 26;
const COLUMN_CONFIG_KEY = 'corryk:watchlist-columns:v2';

type SortKey = 'companyName' | ScreenerRightColumnId;
type SortDir = 'asc' | 'desc';

export function WatchlistPanel({
  tabId,
  panelId,
  initialState,
}: {
  tabId: TabId;
  panelId: PanelId;
  initialState?: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const { data: watchlistsData } = useWatchlists();
  const watchlists = watchlistsData?.data ?? [];
  const globalCurrency = useGlobalCurrency();

  // Column layout shared by the toolbar "Columns" manager and the table headers.
  const [columnConfig, colActions] = useScreenerColumnConfig(COLUMN_CONFIG_KEY);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const columnsBtnRef = useRef<HTMLButtonElement>(null);

  // Initial selection may be seeded by the opener (e.g. Platform Builder's
  // "Show all in Ward" passes the watchlist it just created).
  const [pickedId, setPickedId] = useState<string | null>(
    typeof initialState?.watchlistId === 'string' ? (initialState.watchlistId as string) : null,
  );
  // Select a watchlist on request from elsewhere on this channel (the
  // Prescription panel's "Show all in Ward" drives the linked Ward to the list
  // it just created — or a freshly-opened one reads it on mount).
  const selKey = useCatchmentKey(tabId, panelId);
  const selReq = useSelectLink((s) => s.byKey[selKey] ?? null);
  useEffect(() => {
    if (selReq && selReq.kind === 'watchlist') setPickedId(selReq.id);
  }, [selReq?.nonce, selReq?.kind, selReq?.id]);
  // Auto-fall-back to the first watchlist when nothing is picked or the
  // picked one is gone (e.g. the user just deleted it).
  const selectedId =
    pickedId && watchlists.some((w) => w.id === pickedId) ? pickedId : (watchlists[0]?.id ?? null);

  const { data: rows, isLoading, error } = useWatchlistAsScreener(selectedId);
  const activeCountry = useActiveCountry();
  const [sortKey, setSortKey] = useState<SortKey>('companyName');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const filteredRows = useMemo<ScreenerRow[]>(() => {
    if (!activeCountry) return rows;
    return rows.filter((r) => r.countryCode === activeCountry);
  }, [rows, activeCountry]);

  const sortedRows = useMemo(() => {
    const out = [...filteredRows];
    out.sort((a, b) => cmpRows(a, b, sortKey, sortDir));
    return out;
  }, [filteredRows, sortKey, sortDir]);

  // Export the watchlist (as currently sorted/filtered) to a CSV with EVERY
  // column — even ones the user has hidden — for download / Excel.
  const selectedWatchlist = watchlists.find((w) => w.id === selectedId) ?? null;
  function onExport() {
    if (sortedRows.length === 0) return;
    exportWatchlistCsv(
      sortedRows,
      columnConfig.columns,
      globalCurrency,
      (id) => t(SCREENER_COLUMN_DEFS[id].labelKey),
      selectedWatchlist?.name ?? 'watchlist',
    );
  }

  function onSort(k: SortKey) {
    if (k === sortKey) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(k);
      setSortDir(defaultDirFor(k));
    }
  }

  function onPickWatchlist(w: WatchlistRow) {
    setPickedId(w.id);
  }

  // Row selection — broadcasts to the panel's channel (same semantics
  // as the Screener) so Company Overview etc. update on click.
  const selectedEntityId = usePanelEntityId(tabId, panelId);
  const setPanelEntity = useSetPanelEntity(tabId, panelId);
  const select = (id: string) => setPanelEntity(id);

  // Double-click → open a NEW floating Company Overview pinned to that
  // entity. Mirrors the Screener's behaviour exactly.
  const dockviewApiRef = useDockviewApi();
  const addPanelToStore = useAppStore((s) => s.actions.addPanel);
  const pinPanelAction = useAppStore((s) => s.actions.pinPanel);
  const setPinnedEntity = useAppStore((s) => s.actions.setPanelEntity);
  function openCompanyOverviewFor(entityId: string) {
    const api = dockviewApiRef.current;
    if (!api) return;
    const newId = addPanelToStore(tabId, {
      component: 'company-overview',
      title: 'Record',
    });
    pinPanelAction(tabId, newId);
    setPinnedEntity(tabId, newId, entityId);
    const cascade = api.panels.filter((p) => p.api.location.type === 'floating').length;
    const offset = 24 * cascade;
    api.addPanel({
      id: newId,
      component: 'company-overview',
      title: 'Record',
      params: { tabId, panelId: newId },
      floating: {
        position: { left: 200 + offset, top: 120 + offset },
        width: 720,
        height: 600,
      },
    });
  }

  const showEmpty = !!selectedId && !isLoading && !error && sortedRows.length === 0;
  const emptyMsg = activeCountry
    ? t('panels.watchlists.emptyForMarket', { country: activeCountry })
    : t('panels.watchlists.empty');
  const noSelection = !selectedId;

  // Layout mirrors the Screener exactly — PanelFrame directly + an
  // `auto / 1fr` grid that puts the picker row on top and the
  // two-pane table in the 1fr cell. We deliberately do not use
  // PanelShell here: its BodyRegion wraps children in an
  // overflow:auto box which competes with the two-pane's own
  // right-pane scroll (rightScrollRef), giving the Watchlist a
  // different horizontal scroll position than the Screener. With
  // PanelFrame the two-pane is the sole scrolling element — same
  // wrapper shape as the Screener, same scroll behaviour both axes.
  return (
    <PanelFrame tabId={tabId} panelId={panelId}>
      <div
        style={{
          display: 'grid',
          gridTemplateRows: 'auto 1fr',
          height: '100%',
          minHeight: 0,
        }}
      >
        {/* Picker row — same chrome (padding, borderBottom, background)
            as the Screener's filter bar so the two panels' top rows
            align visually at 1:1. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--s-2)',
            padding: 'var(--s-2) var(--s-3)',
            borderBottom: '1px solid var(--hairline)',
            background: 'var(--surface)',
          }}
        >
          <WatchlistPicker selectedId={selectedId} onSelect={onPickWatchlist} />
          <button
            ref={columnsBtnRef}
            type="button"
            onClick={() => setColumnsOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={columnsOpen}
            title="Show, hide and reorder columns"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--s-1)',
              padding: '2px var(--s-2)',
              fontSize: 'var(--t-body)',
              border: '1px solid var(--hairline)',
              borderRadius: 2,
              background: columnsOpen ? 'var(--color-accent-100)' : 'var(--surface)',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <Columns3 size={12} />
            <span>Columns</span>
          </button>
          <ColumnsMenu
            open={columnsOpen}
            onClose={() => setColumnsOpen(false)}
            anchorRef={columnsBtnRef}
            columns={columnConfig.columns}
            label={(id) => t(SCREENER_COLUMN_DEFS[id].labelKey)}
            actions={colActions}
          />
          <div style={{ flex: 1 }} />
          {/* Export — right of the header, styled like the Record panel's
              Dossier button. Exports the watchlist (current sort/filter) to a
              CSV with every column, hidden ones included. */}
          <button
            type="button"
            onClick={onExport}
            disabled={sortedRows.length === 0}
            title="Export this watchlist to CSV (all columns — opens in Excel)"
            style={{
              height: 22,
              border: '1px solid var(--hairline)',
              background: 'var(--menu-surface)',
              color: 'var(--text-secondary)',
              borderRadius: 4,
              fontSize: 'var(--t-meta)',
              fontWeight: 600,
              padding: '0 8px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              cursor: sortedRows.length === 0 ? 'default' : 'pointer',
              opacity: sortedRows.length === 0 ? 0.5 : 1,
              flexShrink: 0,
            }}
          >
            <Download size={11} /> Export
          </button>
        </div>

        {/* Body — padded wrapper that gives the table a 12px gutter on
            all four sides (between the picker-row separator and the
            top of the table, and between the table and the left/right
            panel chrome). The wrapper itself does NOT scroll; the
            WatchlistTable's right-pane (rightScrollRef) is the sole
            scrolling element. That keeps the Screener's clean single-
            container scroll model intact even though we add chrome
            padding around it.

            One of: no-selection / loading / error / empty / the
            two-pane table. */}
        <div
          style={{
            padding: 'var(--s-3)',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {noSelection ? (
            <PanelEmptyState fill icon={<Star size={18} />} title="Pick a watchlist">
              {t('panels.watchlists.noSelection')}
            </PanelEmptyState>
          ) : isLoading ? (
            <BodyMessage>{t('panelShell.loading')}</BodyMessage>
          ) : error ? (
            <BodyMessage tone="error">{t('panels.watchlists.error')}</BodyMessage>
          ) : showEmpty ? (
            <PanelEmptyState fill icon={<Star size={18} />} title="Start filling this list">
              {emptyMsg}
            </PanelEmptyState>
          ) : (
            <WatchlistTable
              columnConfig={columnConfig}
              colActions={colActions}
              rows={sortedRows}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSort}
              selectedId={selectedEntityId ?? null}
              onSelect={select}
              onOpenOverview={openCompanyOverviewFor}
              watchlistId={selectedId!}
            />
          )}
        </div>
      </div>
    </PanelFrame>
  );
}

function BodyMessage({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone?: 'error' | 'neutral';
}) {
  return (
    <div
      style={{
        padding: 'var(--s-4)',
        color: tone === 'error' ? 'var(--color-negative-500)' : 'var(--text-tertiary)',
      }}
    >
      {children}
    </div>
  );
}

// --- Table ----------------------------------------------------------

interface TableProps {
  columnConfig: ScreenerColumnConfig;
  colActions: ScreenerColumnConfigActions;
  rows: ScreenerRow[];
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (k: SortKey) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onOpenOverview: (id: string) => void;
  watchlistId: string;
}

function WatchlistTable(props: TableProps) {
  const {
    columnConfig,
    colActions,
    rows,
    sortKey,
    sortDir,
    onSort,
    selectedId,
    onSelect,
    onOpenOverview,
    watchlistId,
  } = props;
  const { t } = useTranslation();
  const globalCurrency = useGlobalCurrency();
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const rightScrollRef = useRef<HTMLDivElement>(null);
  const leftBodyContentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => rightScrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  // Mirror the Screener's two-pane scroll sync — right pane scrolls,
  // the left (frozen Name) pane follows via a transform on its
  // virtual-list content. Avoids dual-virtualizer pain.
  useEffect(() => {
    const r = rightScrollRef.current;
    if (!r) return;
    const onScroll = () => {
      if (leftBodyContentRef.current) {
        leftBodyContentRef.current.style.transform = `translateY(-${r.scrollTop}px)`;
      }
    };
    onScroll();
    r.addEventListener('scroll', onScroll, { passive: true });
    return () => r.removeEventListener('scroll', onScroll);
  }, [rows.length]);

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const visibleCols = columnConfig.columns.filter((c) => c.visible);
  const rowGridTemplate = visibleCols.map((c) => `${c.width}px`).join(' ');
  const rightMinWidth = visibleCols.reduce((sum, c) => sum + c.width, 0);

  return (
    <div
      onMouseLeave={() => setHoveredId(null)}
      style={{
        display: 'grid',
        gridTemplateColumns: `${columnConfig.nameWidth}px 1fr`,
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* Frozen left pane — name column */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: 'var(--surface)',
          borderRight: '1px solid var(--hairline)',
          boxShadow: '4px 0 6px -4px rgba(0,0,0,0.08)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <SortHeader
          label="Entity"
          onClick={() => onSort('companyName')}
          isActive={sortKey === 'companyName'}
          dir={sortDir}
          width={columnConfig.nameWidth}
        />
        <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          <div
            ref={leftBodyContentRef}
            style={{ height: totalSize, position: 'relative', willChange: 'transform' }}
          >
            {virtualItems.map((vi) => {
              const row = rows[vi.index]!;
              const isSelected = row.entityId === selectedId;
              const isHovered = !isSelected && row.entityId === hoveredId;
              return (
                <EntityRowContextMenu
                  key={vi.key}
                  entityId={row.entityId}
                  legalName={row.companyName ?? null}
                  inWatchlistId={watchlistId}
                >
                  <div
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'link';
                      e.dataTransfer.setData(ENTITY_MIME, row.entityId);
                      e.dataTransfer.setData('text/plain', row.companyNumber);
                    }}
                    onMouseEnter={() => setHoveredId(row.entityId)}
                    onClick={() => onSelect(row.entityId)}
                    onDoubleClick={() => onOpenOverview(row.entityId)}
                    style={{
                      position: 'absolute',
                      top: vi.start,
                      left: 0,
                      right: 0,
                      height: vi.size,
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 var(--s-3)',
                      background: isSelected
                        ? 'var(--color-accent-100)'
                        : isHovered
                          ? 'var(--hover-subtle)'
                          : 'transparent',
                      borderBottom: '1px solid var(--border-subtle)',
                      cursor: 'pointer',
                      fontSize: 'var(--t-body)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <strong
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                      }}
                    >
                      {row.companyName ?? '—'}
                    </strong>
                  </div>
                </EntityRowContextMenu>
              );
            })}
          </div>
        </div>
      </div>

      {/* Scrollable right pane */}
      <div ref={rightScrollRef} style={{ overflow: 'auto', position: 'relative' }}>
        <div style={{ minWidth: rightMinWidth, position: 'relative' }}>
          {/* Headers */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: rowGridTemplate,
              // Match the body rows' horizontal inset so header columns line up
              // with their cells (the rows below carry padding:0 var(--s-3)).
              padding: '0 var(--s-3)',
              height: 22,
              borderBottom: '2px solid var(--text-primary)',
              background: 'var(--surface-elevated)',
              position: 'sticky',
              top: 0,
              zIndex: 2,
            }}
          >
            {visibleCols.map((col) => (
              <ColumnHeaderCell
                key={col.id}
                col={col}
                columns={columnConfig.columns}
                label={t(SCREENER_COLUMN_DEFS[col.id].labelKey)}
                numeric={SCREENER_COLUMN_DEFS[col.id].numeric}
                active={sortKey === col.id}
                sortDir={sortDir}
                onSort={onSort}
                moveColumn={colActions.moveColumn}
                setColumnWidth={colActions.setColumnWidth}
              />
            ))}
          </div>

          {/* Body */}
          <div style={{ height: totalSize, position: 'relative' }}>
            {virtualItems.map((vi) => {
              const row = rows[vi.index]!;
              const isSelected = row.entityId === selectedId;
              const isHovered = !isSelected && row.entityId === hoveredId;
              return (
                <EntityRowContextMenu
                  key={vi.key}
                  entityId={row.entityId}
                  legalName={row.companyName ?? null}
                  inWatchlistId={watchlistId}
                >
                  <div
                    role="row"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'link';
                      e.dataTransfer.setData(ENTITY_MIME, row.entityId);
                      e.dataTransfer.setData('text/plain', row.companyNumber);
                    }}
                    onClick={() => onSelect(row.entityId)}
                    onDoubleClick={() => onOpenOverview(row.entityId)}
                    onMouseEnter={() => setHoveredId(row.entityId)}
                    style={{
                      position: 'absolute',
                      top: vi.start,
                      left: 0,
                      right: 0,
                      height: vi.size,
                      display: 'grid',
                      gridTemplateColumns: rowGridTemplate,
                      padding: '0 var(--s-3)',
                      fontSize: 'var(--t-body)',
                      borderBottom: '1px solid var(--border-subtle)',
                      background: isSelected
                        ? 'var(--color-accent-100)'
                        : isHovered
                          ? 'var(--hover-subtle)'
                          : 'transparent',
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                      alignItems: 'center',
                    }}
                  >
                    {visibleCols.map((col) => {
                      const def = SCREENER_COLUMN_DEFS[col.id];
                      const cell = renderCell(row, col.id, globalCurrency);
                      return (
                        <div
                          key={col.id}
                          data-numeric={def.numeric || undefined}
                          title={cell.suspectReason}
                          style={{
                            padding: '0 var(--s-2)',
                            textAlign: def.numeric ? 'right' : 'left',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {cell.suspect && (
                            <span
                              aria-label="suspect input"
                              style={{
                                color: 'var(--color-warning-500, #b45309)',
                                marginRight: 4,
                              }}
                            >
                              ⚠
                            </span>
                          )}
                          {cell.display}
                        </div>
                      );
                    })}
                  </div>
                </EntityRowContextMenu>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function SortHeader({
  label,
  onClick,
  isActive,
  dir,
  width,
}: {
  label: string;
  onClick: () => void;
  isActive: boolean;
  dir: SortDir;
  width: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 4,
        height: 22,
        width,
        padding: '0 var(--s-3)',
        background: 'var(--surface-elevated)',
        border: 'none',
        borderBottom: '2px solid var(--text-primary)',
        cursor: 'pointer',
        fontSize: 'var(--t-meta)',
        fontWeight: isActive ? 700 : 500,
        color: isActive ? 'var(--accent)' : 'var(--text-secondary)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      <span>{label}</span>
      {isActive && (dir === 'asc' ? <ChevronUp size={10} /> : <ChevronDown size={10} />)}
    </button>
  );
}

// --- Sorting --------------------------------------------------------

function defaultDirFor(k: SortKey): SortDir {
  // Numeric / scalar fields default to desc (largest first); text fields
  // default to asc — matches the Screener's column behaviour.
  switch (k) {
    case 'turnoverScreener':
    case 'turnoverConfidenceP':
    case 'netAssets':
    case 'employees':
      return 'desc';
    default:
      return 'asc';
  }
}

function cmpRows(a: ScreenerRow, b: ScreenerRow, key: SortKey, dir: SortDir): number {
  const v = compareByKey(a, b, key);
  return dir === 'asc' ? v : -v;
}

function compareByKey(a: ScreenerRow, b: ScreenerRow, key: SortKey): number {
  if (key === 'companyName') return strCmp(a.companyName, b.companyName);
  switch (key) {
    case 'countryCode':
      return strCmp(a.countryCode, b.countryCode);
    case 'region':
      return strCmp(a.region, b.region);
    case 'subsegment':
      return strCmp(a.subsegment, b.subsegment);
    case 'ownershipType':
      return strCmp(a.ownershipType, b.ownershipType);
    case 'tenure':
      // Strongest evidence first (owns > related landlord > landlord > unknown), then
      // by the number of owned sites.
      return (
        tenureRank(a.tenure) - tenureRank(b.tenure) ||
        numCmp(a.tenureSitesOwned, b.tenureSitesOwned)
      );
    case 'qualityTier':
      return strCmp(a.qualityTier, b.qualityTier);
    case 'turnoverScreener':
      return numCmp(a.turnoverScreener, b.turnoverScreener);
    case 'netAssets':
      return numCmp(a.netAssets, b.netAssets);
    case 'employees':
      return numCmp(a.employees, b.employees);
    case 'turnoverConfidenceP':
      // turnoverConfidenceP is a "P{N}" string; sort by N. Null → -1.
      return numCmp(parseP(a.turnoverConfidenceP), parseP(b.turnoverConfidenceP));
    case 'originationSignal':
      return numCmp(a.originationSignal, b.originationSignal);
    case 'sellerReadiness':
      return numCmp(a.sellerReadiness, b.sellerReadiness);
  }
}

const TENURE_ORDER = ['Owns premises', 'Related-party landlord', 'Third-party landlord'];
function tenureRank(t: string | null | undefined): number {
  const i = t ? TENURE_ORDER.indexOf(t) : -1;
  return i === -1 ? TENURE_ORDER.length : i;
}

function strCmp(a: string | null | undefined, b: string | null | undefined): number {
  const A = a ?? '';
  const B = b ?? '';
  return A.localeCompare(B);
}

function numCmp(a: number | null | undefined, b: number | null | undefined): number {
  // Nulls sort to the BOTTOM regardless of direction — they're missing
  // data, not a meaningful "low" value. Achieved by mapping null to
  // -Infinity for desc and +Infinity for asc. We don't know the dir
  // here, so use the absent-value-large trick: nulls always last by
  // treating them as Infinity, then sign-flip at the call site.
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a - b;
}

function parseP(v: string | null | undefined): number | null {
  if (!v) return null;
  const n = Number(v.replace(/^P/, ''));
  return Number.isFinite(n) ? n : null;
}

// --- CSV export -----------------------------------------------------

// Quote every field (guards embedded commas / quotes / newlines); escape an
// internal quote by doubling it. Excel reads this as one cell per field.
function csvCell(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

// Build + download a CSV of the watchlist's rows with EVERY column — including
// ones the user has hidden — in the configured order (Entity + company number
// first). Cell values mirror what the panel renders (renderCell, in the active
// display currency), so the export is what-you-see.
function exportWatchlistCsv(
  rows: ScreenerRow[],
  columns: ScreenerColumnState[],
  currency: GlobalCurrency,
  label: (id: ScreenerRightColumnId) => string,
  watchlistName: string,
): void {
  const clean = (s: string): string => (s === EMPTY_VALUE ? '' : s);
  const header = ['Entity', 'Company number', ...columns.map((c) => label(c.id))];
  const lines = [header.map(csvCell).join(',')];
  for (const row of rows) {
    const cells = [
      row.companyName ?? '',
      row.companyNumber ?? '',
      ...columns.map((c) => clean(renderCell(row, c.id, currency).display)),
    ];
    lines.push(cells.map(csvCell).join(','));
  }
  // Prepend a UTF-8 BOM so Excel renders accented names correctly.
  const csv = '\uFEFF' + lines.join('\r\n');
  const slug =
    watchlistName
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase() || 'watchlist';
  const date = new Date().toISOString().slice(0, 10);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${slug}-corryk-${date}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
